import { Button, Col, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import { UserMngListParams, UserStatus } from '@/types/userMng';
import { useAtomValue } from 'jotai';
import { commonOptionsAtom } from '@/atoms/commonOptions';
import type { FormInstance } from 'antd/es/form';
import { useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { LimitMap } from '../type';

interface SearchFormProps {
  onSearch: (values?: Record<string, any>) => void;
  form: FormInstance;
  showAdvanced: boolean;
  ontoggleAdvanced: () => void;
}

const SearchForm = ({
  onSearch,
  form,
  showAdvanced,
  ontoggleAdvanced,
}: SearchFormProps) => {
  const commonOptions = useAtomValue(commonOptionsAtom);

  return (
    <Form
      form={form}
      size="small"
      onFinish={(values) => onSearch(values)}
      style={{ width: '100%' }}
    >
      <Row gutter={[16, 8]}>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="userNumbers"
            label="用户ID"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Input allowClear placeholder="请输入 Supp ID" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="name"
            label="用户名"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Input allowClear placeholder="请输入用户名" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="userStatus"
            label="状态"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              placeholder="请选择用户状态"
              options={[
                { label: '不限', value: '' },
                { label: '封禁中', value: UserStatus.BANNED },
                { label: '警告中', value: UserStatus.ALERT },
                { label: '正常', value: UserStatus.ACTIVE },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="updateUser"
            label="最近操作"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              showSearch
              placeholder="请选择操作人"
              options={[{ label: '所有员工名称列表', value: '' }]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            label="注册时间"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Space.Compact block>
              <Form.Item name="createTimeStart" noStyle>
                <DatePicker placeholder="开始日期" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="createTimeEnd" noStyle>
                <DatePicker placeholder="结束日期" style={{ width: '100%' }} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="userType"
            label="用户类型"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              placeholder="请选择用户类型"
              options={commonOptions.userTypes.map((type) => ({
                label: type.text,
                value: type.value,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {showAdvanced && (
        <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="invitorUserSuppId"
              label="推荐人ID"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input allowClear placeholder="请输入推荐人 Supp ID" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="invitorName"
              label="推荐人名字"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input allowClear placeholder="请输入推荐人名字" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="hasInvite"
              label="是否有邀请"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                options={[
                  { label: '是', value: true },
                  { label: '否', value: false },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="hasCampus"
              label="校园认证"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                options={[
                  { label: '是', value: true },
                  { label: '否', value: false },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="campusId"
              label="学校ID"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input allowClear placeholder="请输入学校 ID" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="certificated"
              label="艺术家认证"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                options={[
                  { label: '是', value: true },
                  { label: '否', value: false },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="taskCompleted"
              label="新手任务"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                options={[
                  { label: '已完成', value: true },
                  { label: '未完成', value: false },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="email"
              label="邮箱"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input allowClear placeholder="请输入邮箱" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item label="限额状态" name="enableLimit">
              <Select style={{ width: "100%" }} placeholder="请选择">
                {
                  [...LimitMap.keys()].map(key => {
                    const label = LimitMap.get(key);
                    return <Select.Option key={key} value={key}>{label}</Select.Option>
                  })
                }
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row justify="end" style={{ marginTop: 24, marginBottom: 24 }}>
        <Col>
          <Space size="middle">
            <Button onClick={() => form.resetFields()}>重置</Button>
            <Button type="primary" htmlType="submit">
              搜索
            </Button>
            <Button
              type="link"
              onClick={ontoggleAdvanced}
              icon={showAdvanced ? <UpOutlined /> : <DownOutlined />}
            >
              高级搜索
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default SearchForm;
